<template>
    <section class="LeadsAutoAssignerAllocation">
        <el-row type="flex" justify="space-between" align="middle" class="mb-3">
            <el-col>
                <el-checkbox-group v-model="filters" class="text-right">
                    <el-checkbox label="Hide Disabled" border></el-checkbox>
                </el-checkbox-group>
            </el-col>
        </el-row>

        <!-- Table -->
        <div class="table-container table-responsive">
            <el-table
                ref="allocationTable"
                :data="tableData"
                row-key="id"
                @selection-change="handleTableSelectionChange"
                :row-style="rowStyle"
                :header-row-style="headerRowStyle"
                :cell-style="cellStyle"
                border
                stripe
                :fit="true"
                :span-method="objectSpanMethod"
            >
                <ex-table-column type="selection" width="55"></ex-table-column>

                <!-- <ex-table-column prop="id" label="#" :autoFit="true"></ex-table-column> -->

                <ex-table-column prop="broker.full_name" label="Broker" :autoFit="true">
                    <template slot-scope="scope">
                        <strong>{{ scope.row.broker.full_name }}</strong>
                        <br>
                        <span>{{
                                _.size(scope.row.broker.leads_asset_types) > 0 ? _.join(_.map(scope.row.broker.leads_asset_types, 'name'), ', ') : 'All Assets'
                            }}</span>
                        <br>
                        <span
                            v-if="_.get(scope.row, 'broker.leads_loan_amount_min', 0) == 0 && _.get(scope.row, 'broker.leads_loan_amount_max', 0) == 0">Any Value</span>
                        <span v-else>${{
                                _.get(scope.row, 'broker.leads_loan_amount_min', 0)
                            }} – ${{ _.get(scope.row, 'broker.leads_loan_amount_max', '&infin;') }}</span>
                        <br>
                        <span
                            v-if="_.get(scope.row, 'broker.leads_score_min', 0) == 0 && _.get(scope.row, 'broker.leads_score_max', 0) == 0">Any Value</span>
                        <span v-else>{{
                                _.get(scope.row, 'broker.leads_score_min', 0)
                            }} – {{ _.get(scope.row, 'broker.leads_score_max', '&infin;') }}</span>
                    </template>
                </ex-table-column>

                <ex-table-column
                    prop="auto_assigned_leads_count_today"
                    label="Auto Assigned Leads (Today)"
                    :autoFit="true"
                    :fitHeader="true"
                >
                    <template slot-scope="scope">
                        {{ scope.row.auto_assigned_leads_count_today }} /
                        {{ scope.row.broker.leads_quota_daily || '&infin;' }}
                    </template>
                </ex-table-column>

                <ex-table-column prop="total_leads_count" label="Total Leads" :autoFit="true" :fitHeader="true">
                    <template slot-scope="scope">
                        {{ scope.row.total_leads_count }} / {{ scope.row.broker.leads_quota_total || '&infin;' }}
                    </template>
                </ex-table-column>

                <ex-table-column prop="total_applications" label="Total Applications" :autoFit="true" :fitHeader="true">
                    <template slot-scope="scope">
                        {{ scope.row.total_applications }} / {{ scope.row.broker.apps_quota_total || '&infin;' }}
                    </template>
                </ex-table-column>

                <ex-table-column prop="auto_assigned_lead_requests_total" label="Pending Lead Requests" :autoFit="true"
                                 :fitHeader="true">
                    <template slot-scope="scope">
                        {{ scope.row.auto_assigned_lead_requests_total }}
                    </template>
                </ex-table-column>

                <ex-table-column
                    prop="settings.leads_auto_assign_enabled"
                    label="Auto Assign Enabled"
                    align="center"
                    header-align="left"
                    :autoFit="true"
                    :fitHeader="true"
                >
                    <template slot-scope="scope">
                        <el-switch
                            v-model="scope.row.settings.leads_auto_assign_enabled"
                            :width="40"
                            active-color="#67C23A"
                            inactive-color="#F56C6C"
                            :active-value="1"
                            :inactive-value="0"
                            @change="onToggleBrokerSettings(scope.row.id, scope.row)"
                        ></el-switch>
                    </template>
                </ex-table-column>

                <ex-table-column
                    prop="settings.is_available"
                    label="Available"
                    align="center"
                    header-align="left"
                    :autoFit="true"
                    :fitHeader="true"
                >
                    <template slot-scope="scope">
                        <el-switch
                            v-model="scope.row.settings.is_available"
                            :width="40"
                            active-color="#67C23A"
                            inactive-color="#F56C6C"
                            :active-value="1"
                            :inactive-value="0"
                            @change="onToggleBrokerSettings(scope.row.id, scope.row)"
                        ></el-switch>
                    </template>
                </ex-table-column>

                <ex-table-column prop="editButtons" label="" :autoFit="true" fixed="right">
                    <template slot-scope="scope">
                        <el-button size="mini" @click="openBrokerEditorModal(scope.row.id, scope.row)">Edit</el-button>
                        <el-button
                            size="mini"
                            type="success"
                            @click="openBrokerAllocationModal(scope.row.id)"
                            :disabled="!scope.row.settings.leads_auto_assign_enabled && tableSelection.length <= 1"
                        >
                            Allocate
                        </el-button>
                    </template>
                </ex-table-column>
            </el-table>

            <!-- Broker Allocation Modal -->
            <el-dialog title="Broker Allocation" :visible.sync="showBrokerAllocationModal">
                <div>
                    <el-form label-position="top">
                        <el-form-item label="Number of Leads">
                            <el-input-number v-model="allocationRequest.leadAmount" :min="0"></el-input-number>
                        </el-form-item>
                        <el-form-item label="Broker Filters">
                            <el-checkbox v-model="allocationRequest.ignoreFilters" style="font-weight: normal">
                                Ignore filters and assign next lead regardless
                            </el-checkbox>
                        </el-form-item>
                    </el-form>
                </div>

                <span slot="footer" class="dialog-footer">
					<el-button size="small" @click="showBrokerAllocationModal = false">Cancel</el-button>
					<el-button size="small" type="success" @click="submitBrokerAllocationRequest">Confirm</el-button>
				</span>
            </el-dialog>

            <!-- Broker Editor Modal -->
            <x-modal
                v-if="showBrokerEditorModal"
                @cancel="showBrokerEditorModal = false"
                @close="showBrokerEditorModal = false"
                :wWidth="1100"
                :wHeight="500"
                :draggable="true"
                :resizable="true"
                @ok="submitBrokerEditorRequest"
                :okText="'Confirm'"
            >
                <div slot="title" :class="`d-flex justify-content-between align-items-center`">
                    Broker Editor
                </div>

                <div>
                    <el-form label-position="top">
                        <el-tag type="info">{{ editorRequestName }}</el-tag>
                        <span v-if="tableSelection.length > 3">(+ {{ tableSelection.length - 3 }})</span>

                        <el-form-item label="Broker Availability" class="fin-mt-3">
                            <el-select v-model="editorRequest.is_available" placeholder="Select" class="mr-1">
                                <el-option
                                    v-for="e in [
										{ label: 'Yes', value: 1 },
										{ label: 'No', value: 0 },
									]"
                                    :key="e.value"
                                    :value="e.value"
                                    :label="e.label"
                                ></el-option>
                            </el-select>
                            <el-switch
                                v-model="editorRequest.is_available"
                                :width="40"
                                active-color="#67C23A"
                                inactive-color="#F56C6C"
                                :active-value="1"
                                :inactive-value="0"
                            ></el-switch>
                        </el-form-item>

                        <el-form-item label="Broker Schedule">
                            <SchedulePicker
                                @change="updateBrokerSchedule"
                                :value="editorRequest.leads_schedule"
                                :dayRanges="options.dayRanges"
                            />
                        </el-form-item>

                        <el-row :gutter="10" class="mb-5">
                            <el-col :span="8">
                                <el-form-item label="Daily Lead Quota">
                                    <el-input-number class="w-100" v-model="editorRequest.leads_quota_daily"
                                                     :min="0"></el-input-number>
                                    <small>Set to 0 for unlimited</small>
                                </el-form-item>
                            </el-col>
                            <el-col :span="8">
                                <el-form-item label="Total Lead Quota">
                                    <el-input-number class="w-100" v-model="editorRequest.leads_quota_total"
                                                     :min="0"></el-input-number>
                                    <small>Set to 0 for unlimited</small>
                                </el-form-item>
                            </el-col>
                            <el-col :span="8">
                                <el-form-item label="Total Applications Quota">
                                    <el-input-number class="w-100" v-model="editorRequest.apps_quota_total"
                                                     :min="0"></el-input-number>
                                    <small>Set to 0 for unlimited</small>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <el-form-item label="Loan Amounts">
                            <div class="d-flex justify-content-start" style="max-width: 600px">
                                <el-input placeholder="Min. Loan Amount" v-model="editorRequest.leads_loan_amount_min"
                                          type="number">
                                    <template slot="prepend">$</template>
                                </el-input>
                                <span class="mx-2">–</span>
                                <el-input placeholder="Max. Loan Amount" v-model="editorRequest.leads_loan_amount_max"
                                          type="number">
                                    <template slot="prepend">$</template>
                                </el-input>
                            </div>
                            <small>Set min and max to 0 for any loan amounts</small>
                        </el-form-item>

                        <el-form-item label="Lead Score">
                            <div class="d-flex justify-content-start" style="max-width: 600px">
                                <el-input placeholder="Min. Lead Score" v-model="editorRequest.leads_score_min"
                                          type="number">
                                    <template slot="prepend"></template>
                                </el-input>
                                <span class="mx-2">–</span>
                                <el-input placeholder="Max. Lead Score" v-model="editorRequest.leads_score_max"
                                          type="number">
                                    <template slot="prepend"></template>
                                </el-input>
                            </div>
                            <small>Set min and max to 0 for any lead Score</small>
                        </el-form-item>

                        <el-form-item label="Asset Types">
                            <div class="d-flex">
                                <el-select
                                    v-model="editorRequest.leads_asset_types"
                                    placeholder="Select"
                                    multiple
                                    @change="onAssetTypeChange"
                                    style="width: 100%; max-width: 100%; margin-right: 10px"
                                >
                                    <el-option v-for="(e, i) in assetTypes" :key="i" :value="e.id"
                                               :label="e.name"></el-option>
                                </el-select>
                                <!-- <el-button type="success" @click="selectAllAssetTypes">Select All</el-button> -->
                                <el-button type="warning" @click="clearAssetTypes">Clear</el-button>
                            </div>
                            <small>Leave empty to match against all asset types</small>
                        </el-form-item>

                        <el-form-item label="Source Types">
                            <div class="d-flex">
                                <el-select
                                    v-model="editorRequest.leads_source_types"
                                    placeholder="Select"
                                    multiple
                                    @change="onSourceTypeChange"
                                    style="width: 100%; max-width: 100%; margin-right: 10px"
                                >
                                    <el-option v-for="(e, i) in sourceTypes" :key="i" :value="e.id"
                                               :label="e.name"></el-option>
                                </el-select>
                                <!-- <el-button type="success" @click="selectAllAssetTypes">Select All</el-button> -->
                                <el-button type="warning" @click="clearSourceTypes">Clear</el-button>
                            </div>
                            <small>Leave empty to match against all source types</small>
                        </el-form-item>

                        <el-form-item label="Leads State">
                            <small class="d-block">
                                Assign deals to brokers in the selected state
                            </small>

                            <el-select
                                v-model="editorRequest.leads_state"
                                placeholder="Select State"
                                multiple
                                style="width: 100%; max-width: 100%; margin-right: 10px"
                            >
                                <el-option v-for="(e, i) in options.leadStates" :key="i" :value="e.id"
                                           :label="e.name"></el-option>
                            </el-select>
                        </el-form-item>

                        <el-form-item label="Leads Application Type">
                            <small class="d-block">
                                Assign deals to brokers with the selected application type
                            </small>

                            <el-select
                                v-model="editorRequest.leads_application_type"
                                placeholder="Select Application Type"
                                multiple
                                clearable
                                style="width: 100%; max-width: 100%; margin-right: 10px"
                            >
                                <el-option v-for="(e, i) in options.leadsApplicationType" :key="i" :value="e.value"
                                           :label="e.label"></el-option>
                            </el-select>
                        </el-form-item>

                        <el-form-item label="Leads Type">
                            <small class="d-block">
                                Assign deals to brokers with the selected leads type
                            </small>

                            <el-select
                                v-model="editorRequest.leads_type"
                                placeholder="Select Leads Type"
                                multiple
                                clearable
                                style="width: 100%; max-width: 100%; margin-right: 10px"
                            >
                                <el-option v-for="(e, i) in options.leadTypes" :key="i" :value="e.value"
                                           :label="e.label"></el-option>
                            </el-select>
                        </el-form-item>

                        <el-form-item label="Leads Assign Training Leads">
                            <small class="d-block">
                                Assign deals to brokers with the selected leads assign training leads
                            </small>

                            <el-select
                                v-model="editorRequest.leads_assign_training_leads"
                                placeholder="Select Assign Training Leads"
                                clearable
                                style="width: 100%; max-width: 100%; margin-right: 10px"
                            >
                                <el-option v-for="(e, i) in options.leadsAssignTrainingLeads" :key="i" :value="e.value"
                                           :label="e.label"></el-option>
                            </el-select>
                        </el-form-item>

                        <el-form-item label="Teams Enabled Auto Assign"
                                      v-if="Ambition.getGlobal('tenant').teams_enabled">
                            <small class="d-block">
                                Assign deals to brokers with the leads from the selected teams
                            </small>

                            <el-select
                                v-model="editorRequest.leads_teams_enabled_auto_assign"
                                placeholder="Select Teams"
                                multiple
                                clearable
                                style="width: 100%; max-width: 100%; margin-right: 10px"
                            >
                                <el-option v-for="(e, i) in _.get(Ambition, 'Globals.tenant.teams', [])" :key="i"
                                           :value="e.id"
                                           :label="e.name"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-form>
                </div>
            </x-modal>

            <!--            <el-dialog title="Broker Editor"-->
            <!--                       :visible.sync="showBrokerEditorModal"-->
            <!--                       width="50%"-->
            <!--                       :before-close="onCloseBrokerEditorModal">-->
            <!--                -->

            <!--                <span slot="footer" class="dialog-footer">-->
            <!--					<el-button size="small" @click="showBrokerEditorModal = false">Cancel</el-button>-->
            <!--					<el-button size="small" type="success" @click="submitBrokerEditorRequest">Confirm</el-button>-->
            <!--				</span>-->
            <!--            </el-dialog>-->
        </div>
    </section>
</template>

<script>
import {mapState, mapActions, mapMutations} from "vuex"
import _ from "lodash"

import SchedulePicker from "@/components/LeadsAutoAssigner/shared/SchedulePicker"

export default {
    components: {
        SchedulePicker,
    },
    props: {},
    data() {
        return {
            rowStyle: {
                fontSize: "12px",
            },
            headerRowStyle: {
                fontSize: "12px",
            },
            cellStyle: {},
            tableSelection: [],
            showBrokerAllocationModal: false,
            newAllocationRequest: {brokerId: null, leadAmount: 1, ignoreFilters: false},
            allocationRequest: {},
            showBrokerEditorModal: false,
            editorRequest: {},
            editorRequestName: '',
            brokerSchedule: [],
            filters: [],
            leadsState: []
        }
    },
    computed: {
        ...mapState(["settings", "options", "allocation"]),
        windowSize() {
            const scaleH = 0.9
            const scaleW = 0.9
            return {wWidth: window.innerWidth * scaleW, wHeight: window.innerHeight * scaleH}
        },
        assetTypes() {
            return _.get(this.options, "assetTypes", [])
        },
        sourceTypes() {
            return _.get(this.options, "sourceTypes", [])
        },
        tableData() {
            let list = this.allocation

            if (!list || list.length === 0) return []

            if (this.filters.includes("Hide Disabled")) {
                list = list.filter((e) => e.settings.leads_auto_assign_enabled)
            }
            return list
        }
    },
    watch: {},
    mounted() {
        this.getTeams()
    },
    updated() {
    },
    activated() {
    },
    methods: {
        ///////////////////////////////////////////
        // Table
        ///////////////////////////////////////////
        objectSpanMethod({row, column, rowIndex, columnIndex}) {
            // console.log("objectSpanMethod", { row, column, rowIndex, columnIndex })
            const editButtonsRowSpan = Array.isArray(this.allocation) ? this.allocation.length : 1

            if (column.property === "editButtons" && this.tableSelection.length > 1) {
                return {rowspan: editButtonsRowSpan, colspan: 1}
            }

            return {rowspan: 1, colspan: 1}
        },
        getTeams() {
            this.$tenant
                .post(`/api/lead-management/teams`)
                .then((res) => {
                    console.log('getTeams ', res)
                })
                .catch((err) => err.output())
        },
        handleTableSelectionChange(value) {
            console.log("TABLE SELECTION CHANGED", value)
            this.tableSelection = value
            if (value.length === 0) this.$refs.allocationTable.clearSelection()
        },

        ///////////////////////////////////////////
        // Broker Allocation
        ///////////////////////////////////////////
        openBrokerAllocationModal(brokerId = null) {
            const broker = this.allocation.find((e) => e.id === brokerId)

            const lastRequest = _.get(broker, "auto_assigned_lead_requests.0")
            const requestTotal = _.get(broker, "auto_assigned_lead_requests_total")

            let requestData = {
                brokerId,
            }

            if (lastRequest) {
                requestData = Object.assign(requestData, {
                    leadAmount: requestTotal,
                    ignoreFilters: lastRequest.ignore_filters === 1 ? true : false,
                })
            }

            this.allocationRequest = Object.assign({}, this.newAllocationRequest, requestData)
            this.showBrokerAllocationModal = true
        },

        submitBrokerAllocationRequest() {
            console.log("submitBrokerAllocationRequest", this.allocationRequest)
            this.$store.dispatch("submitBrokerAllocationRequest", this.allocationRequest)
            this.showBrokerAllocationModal = false
        },

        ///////////////////////////////////////////
        // Broker Editor
        ///////////////////////////////////////////
        openBrokerEditorModal(brokerId, row) {
            const index = this.allocation.findIndex((e) => e.id == brokerId)
            const broker = row
            const brokerSettings = broker.settings

            this.tableSelection = [broker]
            this.editorRequestName = _.get(row.broker, 'full_name')

            this.editorRequest = Object.assign({}, brokerSettings, {
                leads_asset_types: brokerSettings.leads_asset_types.map((e) => e.id),
                leads_source_types: brokerSettings.leads_source_types.map((e) => e.id),
                leads_application_type: !_.isEmpty(brokerSettings.leads_application_type) ? brokerSettings.leads_application_type : [],
                leads_assign_training_leads: brokerSettings.leads_assign_training_leads,
                leads_type: !_.isEmpty(brokerSettings.leads_type) ? brokerSettings.leads_type : [],
                leads_state: !_.isEmpty(brokerSettings.leads_state) ? brokerSettings.leads_state : [],
                leads_teams_enabled_auto_assign: !_.isEmpty(brokerSettings.leads_teams_enabled_auto_assign) ? brokerSettings.leads_teams_enabled_auto_assign : []
            })

            this.showBrokerEditorModal = true
        },

        onCloseBrokerEditorModal() {
            this.handleTableSelectionChange([])
        },

        submitBrokerEditorRequest() {
            console.log("submitBrokerEditorRequest", this.editorRequest)

            const brokerIds = this.tableSelection.map((e) => e.id)
            const settings = this.editorRequest
            console.log("submitBrokerEditorRequest", {brokerIds, settings})

            this.$store.dispatch("setBrokerSettings", {brokerIds, settings})
            this.editorRequest = {}
            this.tableSelection = []
            this.showBrokerEditorModal = false
        },

        updateBrokerSchedule(schedule) {
            console.log("updateBrokerSchedule", schedule)
            this.editorRequest.leads_schedule = schedule
        },

        onToggleBrokerSettings(brokerId, {settings}) {
            console.log("onToggleEnabled", brokerId, settings)
            this.$store.dispatch("setBrokerSettings", {brokerIds: [brokerId], settings})
        },

        onAssetTypeChange(value) {

        },

        onSourceTypeChange(value) {

        },

        clearAssetTypes() {
            this.$set(this.editorRequest, "leads_asset_types", [])
        },

        clearSourceTypes() {
            this.$set(this.editorRequest, "leads_source_types", [])
        },

        selectAllAssetTypes() {
            // prettier-ignore
            this.$set(this.editorRequest, "leads_asset_types", this.assetTypes.map(e => e.id))
        },
    },
}
</script>

<style lang="scss" scoped>
.table-container {
    width: 100%;
    overflow-x: scroll !important;

    .el-table {
        min-width: 1000px;
    }
}
</style>

<style lang="scss">
.LeadsAutoAssignerAllocation {
    .el-table .cell,
    .el-table th > .cell {
        white-space: nowrap !important;
        width: fit-content !important;
    }

    .el-dialog__wrapper {
        background-color: rgba(#333, 0.75);
    }

    .el-dialog__header {
        background-color: #2493ef;
        padding: 20px;

        .el-dialog__title {
            color: #fff;
        }
    }
}
</style>
